import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Checkbox from 'components/Checkbox/Checkbox';
import NumberField from 'components/NumberField/NumberField';
import Select from 'components/Select/Select';
import TextField from 'components/TextField/TextField';
import {
  AIR, ARCTIC_TRUCK, DISTANCE, MONETARY_VOLUMES, QUANTITY, RAIL,
  RIGID_TRUCK, SEA, TRUCK, UNIT_CURRENCY, UNIT_MONETARY_VOLUMES, VAN,
} from 'constants/formFields';

import type { FieldPath, FieldValues } from 'react-hook-form';
import type IFieldError from 'store/types/IFieldError';
import type IGoodsSupplierConnectionFields from './interfaces/IGoodsSupplierConnectionFields';

import styles from './sass/GoodsSupplierConnectionFields.module.scss';

// The editable body of a goods-supplier connection. Both places that write these edges — the
// board's connection modal and the `goodsSupplierConnections` list on a record — carry the same
// set of fields, so they share these controls and differ only in where the fields live.
function GoodsSupplierConnectionFields<TFieldValues extends FieldValues>({
  control,
  enumData,
  fieldName = (field) => field as FieldPath<TFieldValues>,
}: Readonly<IGoodsSupplierConnectionFields<TFieldValues>>) {
  const { t } = useTranslation();
  // Anchors the selects' absolutely positioned menus, which would otherwise be clipped by
  // whichever scroll container the host renders these fields in.
  const containerRef = useRef<HTMLDivElement>(null);

  const transportOptions = [
    { label: t('valueChainMap.air'), name: AIR },
    { label: t('valueChainMap.van'), name: VAN },
    { label: t('valueChainMap.rigidTruck'), name: RIGID_TRUCK },
    { label: t('valueChainMap.sea'), name: SEA },
    { label: t('valueChainMap.truck'), name: TRUCK },
    { label: t('valueChainMap.arcticTruck'), name: ARCTIC_TRUCK },
    { label: t('valueChainMap.rail'), name: RAIL },
  ];

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.transportContainer}>
        <span className={styles.transportLabel}>
          {t('valueChainMap.transportMethod')}
        </span>
        <div className={styles.transportOptions}>
          {transportOptions.map((option) => (
            <div key={option.name} className={styles.checkboxWrapper}>
              <div className={styles.checkbox}>
                <Controller
                  control={control}
                  name={fieldName(option.name)}
                  render={({ field: { name, onChange, value } }) => (
                    <Checkbox
                      name={name}
                      checked={value as boolean}
                      value={String(value)}
                      onChange={onChange}
                      label={(<span>{option.label}</span>)}
                    />
                  )}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.distanceWrapper}>
        <Controller
          control={control}
          name={fieldName(DISTANCE)}
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <TextField
              className={styles.field}
              name={name}
              value={value as string}
              error={error?.message}
              onChange={onChange}
              label={t('valueChainMap.distance')}
              maxLength={64}
            />
          )}
        />
      </div>

      <div className={styles.monetaryVolumesWrapper}>
        <div className={styles.leftWrapper}>
          <Controller
            control={control}
            name={fieldName(MONETARY_VOLUMES)}
            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
              <NumberField
                className={styles.fieldTextField}
                name={name}
                value={value as string}
                label={t('valueChainMap.monetaryVolumes')}
                error={error?.message}
                onChange={(values) => onChange(values.value)}
                placeholder={t('valueChainMap.monetaryVolumes')}
                allowNegative={false}
              />
            )}
          />
          <Controller
            control={control}
            name={fieldName(UNIT_MONETARY_VOLUMES)}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Select
                className={styles.fieldSelect}
                options={enumData?.currency}
                placeholder={t('valueChainMap.currency')}
                value={value ?? ''}
                onChange={onChange}
                menuPosition="absolute"
                modalRef={containerRef}
                maxMenuHeight={150}
                error={(error as IFieldError)?.value?.message}
                hideErrorText
              />
            )}
          />
        </div>
        <div className={styles.rightWrapper}>
          <Controller
            control={control}
            name={fieldName(QUANTITY)}
            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
              <NumberField
                className={styles.fieldTextField}
                name={name}
                value={value as string}
                label={t('valueChainMap.quantity')}
                error={error?.message}
                onChange={(values) => onChange(values.value)}
                placeholder={t('valueChainMap.quantity')}
                allowNegative={false}
              />
            )}
          />
          <Controller
            control={control}
            name={fieldName(UNIT_CURRENCY)}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Select
                className={styles.fieldSelect}
                options={enumData?.volumeQtyUnit}
                placeholder={t('valueChainMap.unit')}
                value={value ?? ''}
                onChange={onChange}
                menuPosition="absolute"
                modalRef={containerRef}
                maxMenuHeight={150}
                hideErrorText
                error={(error as IFieldError)?.value?.message}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}

export default GoodsSupplierConnectionFields;
